/**
 * rev 17b addendum F1 ("confirm honesty") — both confirm dialogs (UnlinkOnlyDialog, BulkForceDialog)
 * read system state INTO the confirm ceremony itself, not just the result panel after the fact. A
 * danger-framed "this happens immediately" body is a LIE while the license-removal system is off or
 * dry-run — the write scopes aren't even ticked yet. `usePoolWriteIsLive()` is the single source both
 * dialogs read; `<PoolWriteModeNotice/>` is the matching headline, rendered ABOVE the danger body copy.
 *
 * Deliberately fail-QUIET, not fail-scary: while the state hasn't loaded yet (or errored), the notice
 * renders nothing rather than guessing either way — an operator who can't see the real state should not
 * be shown a false "this is live" ceremony, but should also not be told "this is a dry run" if it isn't.
 */
import { useTranslation } from "react-i18next";
import { usePoolSystemState } from "@/features/pool/hooks";

/** null = unknown (state still loading or errored) — the caller must not claim live OR dry-run/off. */
export function usePoolWriteIsLive(): boolean | null {
  const stateQ = usePoolSystemState();
  if (stateQ.isPending || stateQ.isError) return null;
  return stateQ.data.state === "live";
}

export function PoolWriteModeNotice() {
  const { t } = useTranslation();
  const isLive = usePoolWriteIsLive();
  if (isLive !== false) return null; // only the CONFIRMED off/dry-run case shows the notice
  return (
    <p
      role="status"
      className="rounded-(--radius) border border-(--border) bg-(--info-subtle) px-3 py-2 text-(length:--text-sm) font-medium text-(--info)"
    >
      {t("pool.users.bulk.dryRunHeadline")}
    </p>
  );
}
